import { useState, useEffect, useCallback, useMemo, useRef, lazy, Suspense } from 'react';
import type { Screen, DensityPreset } from './types';

import { Home } from './components/Home';
import { Creator } from './components/Creator';
import { CreatorSetup, type CreatorInit } from './components/CreatorSetup';
import { Tutorial } from './components/Tutorial';
import { LabScreen } from './components/LabScreen';
import { SetupScreen } from './components/SetupScreen';
import { History } from './components/History';
import { Game } from './components/Game';
import { PuzzleLabGame } from './components/PuzzleLabGame';
import { HowToPlay } from './components/HowToPlay';
import { LevelCompletePopup } from './components/LevelCompletePopup';
import { ConsentScreen } from './components/ConsentScreen';
import { TopBar } from './components/TopBar';
import { UpdateBanner } from './components/UpdateBanner';
import { SceneLayout, TransitionCanvas, useSceneTransition, useViewportAtLeast, type TransitionConfig } from '@org/scene-layout';
import { CROSS_PROMO_SRC, useCrossPromoSlot } from './adManager';
import { PlaySetupScreen, type GameSettingsOverride } from './components/PlaySetupScreen';
import { LEVEL_DATA } from './puzzles';
import { isSubPuzzleComplete, useGameSettings } from './store';
import { resolvePuzzle } from './puzzles/multiPuzzleUtils';
import type { LevelTrack, PuzzleDifficulty, PuzzleEntry } from './types';
import * as ShareService from './shareService';
import * as historyService from './historyService';

/* ---------- Dev-only screens (tree-shaken in production) ---------- */
const Admin = import.meta.env.DEV
  ? lazy(() => import('./components/Admin').then((m) => ({ default: m.Admin })))
  : null;
const DesignSystem = import.meta.env.DEV
  ? lazy(() =>
      import('./components/DesignSystem').then((m) => ({
        default: m.DesignSystem,
      }))
    )
  : null;
const CardDesigner = import.meta.env.DEV
  ? lazy(() =>
      import('./components/CardDesigner').then((m) => ({
        default: m.CardDesigner,
      }))
    )
  : null;
const StateEditor = import.meta.env.DEV
  ? lazy(() =>
      import('./components/StateEditor').then((m) => ({
        default: m.StateEditor,
      }))
    )
  : null;
const GridTester = import.meta.env.DEV
  ? lazy(() =>
      import('./components/GridTester').then((m) => ({
        default: m.GridTester,
      }))
    )
  : null;
const LevelManifestEditor = import.meta.env.DEV
  ? lazy(() =>
      import('./components/LevelManifestEditor').then((m) => ({
        default: m.LevelManifestEditor,
      }))
    )
  : null;
import { TUTORIAL_LESSONS } from './tutorialData';
import { useGameStore } from './store';
import { grantConsent, denyConsent, trackEvent } from './analytics';
import { isEnabledQueryFlag } from './queryFlags';
import { startMusic, ensureAudioContext, onMusicToggle } from './sounds';
import {
  createTrainSeed,
  nextTrainSeed,
  normalizeTrainSeedInput,
} from './trainSeed';

/* ---------- Transition config ---------- */
// Change `type` to swap transitions: 'sliding-door' | 'fade' | 'iris' | 'iris-hole' | 'slide' | 'none'.
// 'iris-hole' matches the nonogram's legacy CSS radial iris.
const SCREEN_TRANSITION: TransitionConfig = { type: 'iris-hole', durationMs: 893 };

/* ---------- Layout config ---------- */
const GAME_MAX_W = 728;
// Matches legacy AdLayout: rails appear once viewport is wide enough for them.
const RAILS_BREAKPOINT = 1096;

/* Scenes where the inner content should stretch to fill (game boards, shared play). */
function sceneOverflow(screen: Screen): 'auto' | 'hidden' {
  if (screen.type === 'level' || screen.type === 'play') return 'hidden';
  return 'auto';
}

/* ---------- URL ↔ Screen mapping ---------- */

const BASE_PATH = import.meta.env.BASE_URL.replace(/\/$/, '');

/** Utility query flags that should persist across navigations */
const PERSISTENT_PARAMS = ['skip_consent_popup', 'sound_off', 'effects_off', 'show_ad_areas'];
const _initialParams = new URLSearchParams(window.location.search);
const _persistentSearch = PERSISTENT_PARAMS
  .filter((k) => _initialParams.has(k))
  .map((k) => `${k}=${_initialParams.get(k)}`)
  .join('&');

function isWithinBasePath(pathname: string): boolean {
  return pathname === BASE_PATH || pathname.startsWith(`${BASE_PATH}/`);
}

/* ---------- Tutorial auto-launch ---------- */

const TUTORIAL_DONE_KEY = 'nonogram_tutorial_done';

type LegacySharedLevelScreen = {
  type: 'level';
  mode: 'shared';
  slug: string;
  entry?: PuzzleEntry;
};

function isLegacySharedLevelScreen(
  screen: Screen | LegacySharedLevelScreen,
): screen is LegacySharedLevelScreen {
  return screen.type === 'level' && screen.mode === 'shared';
}

function isTutorialDone(): boolean {
  try {
    return localStorage.getItem(TUTORIAL_DONE_KEY) === '1';
  } catch {
    return false;
  }
}

function markTutorialDone(): void {
  try {
    localStorage.setItem(TUTORIAL_DONE_KEY, '1');
  } catch {
    /* noop */
  }
}

function screenToPath(s: Screen | LegacySharedLevelScreen): string {
  let path: string;
  switch (s.type) {
    case 'home':
      path = '/';
      break;
    case 'tutorial':
      path = s.lesson != null ? `/tutorial?lesson=${s.lesson}` : '/tutorial';
      break;
    case 'levels-menu':
      path = '/';
      break;
    case 'setup':
      path = '/setup';
      break;
    case 'lab-setup':
      path = '/practice';
      break;
    case 'admin':
      path = import.meta.env.DEV ? '/admin' : '/';
      break;
    case 'design':
      path = import.meta.env.DEV ? '/admin/design' : '/';
      break;
    case 'admin-card-designer':
      path = import.meta.env.DEV ? '/admin/card-designer' : '/';
      break;
    case 'admin-state-editor':
      path = import.meta.env.DEV ? '/admin/state-editor' : '/';
      break;
    case 'admin-grid-tester':
      path = import.meta.env.DEV ? '/admin/grid-tester' : '/';
      break;
    case 'admin-levels':
      path = import.meta.env.DEV ? `/admin/levels/${s.track}` : '/';
      break;

    case 'creator':
      path = '/create';
      break;
    case 'creator-editor':
      path = '/create/level';
      break;
    case 'history':
      path = '/history';
      break;

    case 'play':
      path = '/shared';
      break;

    case 'level': {
      if (isLegacySharedLevelScreen(s)) {
        path = '/';
        break;
      }
      if (s.mode === 'multi-sub') {
        const parts = [`puzzle=${s.levelIndex}`, `sub=${s.subIndex}`];
        if (s.track !== 'progression') parts.push(`track=${s.track}`);
        if (s.difficulty !== 'casual') parts.push(`diff=${s.difficulty}`);
        path = `/level?${parts.join('&')}`;
        break;
      }
      const params = new URLSearchParams();
      if (s.mode === 'levels') {
        params.set('puzzle', String(s.puzzleIndex));
      } else {
        params.set('mode', 'train');
        params.set('size', String(s.size));
        params.set('seed', String(s.seed));
        params.set('density', s.density);
        if (s.strict) params.set('strict', '1');
        params.set('hints', String(s.hintCount));
        params.set('validate', String(s.validateCount));
        if (s.autoMark) params.set('automark', '1');
      }
      path = `/level?${params.toString()}`;
      break;
    }
  }
  const full = path === '/' ? `${BASE_PATH}/` : `${BASE_PATH}${path}`;
  if (!_persistentSearch) return full;
  const sep = full.includes('?') ? '&' : '?';
  return `${full}${sep}${_persistentSearch}`;
}

function pathToScreen(path: string, search: string): Screen {
  const stripped = BASE_PATH
    ? path.replace(new RegExp(`^${BASE_PATH}`), '')
    : path;
  const p = stripped.replace(/\/+$/, '') || '/';

  let m: RegExpMatchArray | null;

  if (p === '/') {
    if (!isTutorialDone()) {
      return { type: 'tutorial', lesson: 0 };
    }
    return { type: 'home' };
  }
  if (p === '/tutorial') {
    const params = new URLSearchParams(search);
    const lessonStr = params.get('lesson');
    if (lessonStr != null) {
      const i = Number(lessonStr);
      if (i >= 0 && i < TUTORIAL_LESSONS.length)
        return { type: 'tutorial', lesson: i };
    }
    return { type: 'tutorial' };
  }

  if (p === '/levels') return { type: 'home' };

  // Legacy /levels/:index — redirect to unified /level route
  m = p.match(/^\/levels\/(\d+)$/);
  if (m) {
    const i = Number(m[1]);
    if (i >= 0 && i < LEVEL_DATA.progression.casual.length)
      return { type: 'level', mode: 'levels', puzzleIndex: i, entry: 'fresh' };
    return { type: 'home' };
  }

  if (import.meta.env.DEV) {
    if (p === '/admin') return { type: 'admin' };
    if (p === '/design' || p === '/admin/design' || p === '/ui-demo' || p === '/admin/ui-demo')
      return { type: 'design' };
    if (p === '/admin/card-designer') return { type: 'admin-card-designer' };
    if (p === '/admin/state-editor') return { type: 'admin-state-editor' };
    if (p === '/admin/grid-tester') return { type: 'admin-grid-tester' };
    if (p === '/admin/levels/progression')
      return { type: 'admin-levels', track: 'progression' };
    if (p === '/admin/levels/daily')
      return { type: 'admin-levels', track: 'daily' };
  }
  if (p === '/setup') return { type: 'setup' };
  if (p === '/create') return { type: 'creator' };
  if (p === '/create/level') return { type: 'creator-editor' };
  if (p === '/history') return { type: 'history' };
  if (p === '/practice') return { type: 'lab-setup' };

  if (p === '/level') {
    const params = new URLSearchParams(search);

    const puzzleStr = params.get('puzzle');
    if (puzzleStr != null) {
      const i = Number(puzzleStr);
      const subStr = params.get('sub');
      // Back-compat: `daily=1` still implies daily track.
      const legacyDaily = params.get('daily') === '1';
      const trackParam = params.get('track');
      const track: LevelTrack =
        trackParam === 'daily' || legacyDaily ? 'daily' : 'progression';
      const diffParam = params.get('diff');
      const difficulty: PuzzleDifficulty =
        diffParam === 'advanced' ? 'advanced' : 'casual';
      if (subStr != null) {
        const si = Number(subStr);
        const pool = LEVEL_DATA[track][difficulty];
        if (i >= 0 && i < pool.length) {
          const level = pool[i];
          if (si >= 0 && si < level.puzzles.length) {
            return {
              type: 'level',
              mode: 'multi-sub',
              track,
              difficulty,
              levelIndex: i,
              subIndex: si,
              entry: 'fresh',
            };
          }
        }
        return { type: 'home' };
      }
      if (i >= 0 && i < LEVEL_DATA.progression.casual.length)
        return {
          type: 'level',
          mode: 'levels',
          puzzleIndex: i,
          entry: 'fresh',
        };
      return { type: 'home' };
    }

    if (params.get('mode') === 'train') {
      const seed = normalizeTrainSeedInput(params.get('seed') ?? '');

      return {
        type: 'level',
        mode: 'train',
        size: Number(params.get('size')) || 5,
        seed: seed || createTrainSeed(),
        density: (params.get('density') as DensityPreset) || 'normal',
        strict: params.get('strict') === '1',
        hintCount: Number(params.get('hints')) || 3,
        validateCount: Number(params.get('validate')) || -1,
        autoMark: params.get('automark') === '1',
        entry: 'fresh',
      };
    }

    return { type: 'home' };
  }

  if (p === '/shared') {
    if (!search || !new URLSearchParams(search).has('d')) return { type: 'home' };
    return { type: 'play', search };
  }

  return { type: 'home' };
}

function normalizeScreenState(screen: Screen | LegacySharedLevelScreen): Screen {
  if (isLegacySharedLevelScreen(screen)) return { type: 'home' };
  if (screen.type !== 'level') return screen;
  if (
    screen.entry === 'fresh' ||
    screen.entry === 'resume' ||
    screen.entry === 'view'
  )
    return screen;
  return { ...screen, entry: 'fresh' };
}

function readScreenFromHistoryOrLocation(): Screen {
  const fallback = pathToScreen(
    window.location.pathname,
    window.location.search
  );
  const state = window.history.state;

  if (!state || typeof state !== 'object' || !('type' in state)) {
    return fallback;
  }

  try {
    return screenToPath(state as Screen) === screenToPath(fallback)
      ? normalizeScreenState(state as Screen)
      : fallback;
  } catch {
    return fallback;
  }
}

function useViewport() {
  const [vp, setVp] = useState(() => ({
    vw: typeof window !== 'undefined' ? window.innerWidth : 0,
    vh: typeof window !== 'undefined' ? window.innerHeight : 0,
  }));
  useEffect(() => {
    const update = () => setVp({ vw: window.innerWidth, vh: window.innerHeight });
    window.addEventListener('resize', update);
    return () => window.removeEventListener('resize', update);
  }, []);
  return vp;
}

export function App() {
  const [screen, setScreenRaw] = useState<Screen>(() =>
    readScreenFromHistoryOrLocation()
  );
  const [showHelp, setShowHelp] = useState(false);
  const consent = useGameStore((s) => s.consent);
  const effectsEnabled = useGameStore((s) => s.effectsEnabled);
  const [showConsent, setShowConsent] = useState(() => {
    const params = new URLSearchParams(window.location.search);
    return (
      consent === null && !isEnabledQueryFlag(params, 'skip_consent_popup')
    );
  });

  const [gridDirty, setGridDirty] = useState(false);
  const [resetTrigger, setResetTrigger] = useState(0);
  const [creatorInit, setCreatorInit] = useState<CreatorInit | null>(() => {
    try {
      const raw = localStorage.getItem('__creator_init_handoff');
      if (raw) {
        localStorage.removeItem('__creator_init_handoff');
        return JSON.parse(raw) as CreatorInit;
      }
    } catch { /* noop */ }
    return null;
  });
  const { phase, wrapNavigate, cancel } = useSceneTransition({
    durationMs: SCREEN_TRANSITION.durationMs ?? 600,
    disabled: SCREEN_TRANSITION.type === 'none',
  });

  const setScreen = useCallback(
    (s: Screen) => {
      const nextScreen = normalizeScreenState(s);
      wrapNavigate(() => {
        setScreenRaw(nextScreen);
        setGridDirty(false);
        window.history.pushState(nextScreen, '', screenToPath(nextScreen));
      });
    },
    [wrapNavigate]
  );

  const replaceScreen = useCallback((s: Screen) => {
    setScreenRaw(normalizeScreenState(s));
  }, []);

  useEffect(() => {
    const params = new URLSearchParams(window.location.search);

    if (isEnabledQueryFlag(params, 'skip_consent_popup')) {
      useGameStore.getState().saveConsent('denied');
      denyConsent();
      setShowConsent(false);
    }

    if (isEnabledQueryFlag(params, 'sound_off')) {
      useGameStore.setState({ soundEnabled: false });
    }

    if (isEnabledQueryFlag(params, 'effects_off')) {
      useGameStore.setState({ effectsEnabled: false });
    }
  }, []);

  useEffect(() => {
    // Restore previously granted consent so GA4 runs in full mode on revisit
    if (useGameStore.getState().consent === 'granted') grantConsent();
  }, []);

  /* ---------- Background music (start on first interaction) ---------- */

  useEffect(() => {
    if (!isWithinBasePath(window.location.pathname)) {
      return;
    }

    let started = false;
    const removeListeners = () => {
      window.removeEventListener('click', tryStart);
      window.removeEventListener('pointerdown', tryStart);
      window.removeEventListener('keydown', tryStart);
    };
    const tryStart = async () => {
      if (started) return;
      started = true;
      try {
        await ensureAudioContext();
        await startMusic();
        removeListeners();
      } catch {
        // Autoplay still blocked — allow retry on next interaction
        started = false;
      }
    };
    window.addEventListener('click', tryStart);
    window.addEventListener('pointerdown', tryStart);
    window.addEventListener('keydown', tryStart);
    return removeListeners;
  }, []);

  useEffect(() => {
    const onPop = () => {
      cancel();
      setScreenRaw(readScreenFromHistoryOrLocation());
      setGridDirty(false);
    };
    window.addEventListener('popstate', onPop);
    return () => window.removeEventListener('popstate', onPop);
  }, [cancel]);

  useEffect(() => {
    window.history.replaceState(screen, '', screenToPath(screen));
  }, [screen]);

  /* ---------- Analytics: screen view ---------- */

  useEffect(() => {
    const params: Record<string, unknown> = { screen_type: screen.type };
    if (screen.type === 'level') {
      params.mode = screen.mode;
      if (screen.mode === 'levels') params.level_index = screen.puzzleIndex;
      if (screen.mode === 'multi-sub') {
        params.level_index = screen.levelIndex;
        params.sub_index = screen.subIndex;
      }
      if (screen.mode === 'train') {
        params.size = screen.size;
        params.density = screen.density;
      }
    }
    if (screen.type === 'tutorial') {
      params.lesson = screen.lesson;
    }
    trackEvent('screen_view', params);
  }, [screen]);

  /* ---------- Mute audio on admin pages ---------- */

  const isAdminScreen =
    screen.type === 'admin' ||
    screen.type === 'design' ||
    screen.type === 'admin-card-designer' ||
    screen.type === 'admin-state-editor' ||
    screen.type === 'admin-grid-tester' ||
    screen.type === 'admin-levels' ||
    screen.type === 'creator' ||
    screen.type === 'creator-editor';

  useEffect(() => {
    if (!isAdminScreen) return;
    const store = useGameStore.getState();
    const prevSound = store.soundEnabled;
    const prevMusic = store.musicEnabled;
    if (prevSound) store.toggleSound();
    if (prevMusic) {
      store.toggleMusic();
      onMusicToggle(false);
    }
    return () => {
      const s = useGameStore.getState();
      if (prevSound && !s.soundEnabled) s.toggleSound();
      if (prevMusic && !s.musicEnabled) {
        s.toggleMusic();
        onMusicToggle(true);
      }
    };
  }, [isAdminScreen]);

  /* ---------- Consent ---------- */

  const handleConsentDecision = useCallback((status: 'granted' | 'denied') => {
    useGameStore.getState().saveConsent(status);
    if (status === 'granted') {
      grantConsent();
      trackEvent('consent_decision', { action: 'granted' });
    } else {
      denyConsent();
      trackEvent('consent_decision', { action: 'denied' });
    }
    setShowConsent(false);
  }, []);

  const handleAnalyticsToggle = useCallback(() => {
    setShowConsent(true);
  }, []);

  /* ---------- TopBar config ---------- */

  const handleRestart = useCallback(() => setResetTrigger((t) => t + 1), []);
  const syncTrainSettings = useCallback(
    (trainScreen: Extract<Screen, { type: 'level'; mode: 'train' }>) => {
      const store = useGameStore.getState();
      const nextSettings = {
        ...store.trainSettings,
        size: trainScreen.size,
        density: trainScreen.density,
        seed: trainScreen.seed,
        strict: trainScreen.strict,
        hintCount: trainScreen.hintCount,
        validateCount: trainScreen.validateCount,
        autoMark: trainScreen.autoMark,
      };

      store.saveTrainSettings(nextSettings);
    },
    []
  );

  useEffect(() => {
    if (screen.type !== 'level' || screen.mode !== 'train') return;
    syncTrainSettings(screen);
  }, [screen, syncTrainSettings]);

  const topBarConfig = ((): {
    navMode: 'disabled' | 'back' | 'exit';
    onBack: () => void;
    confirmExit?: boolean;
    onRestart?: () => void;
  } => {
    switch (screen.type) {
      case 'home':
      case 'levels-menu':
        return { navMode: 'disabled', onBack: () => {} };
      case 'admin':
        return { navMode: 'back', onBack: () => setScreen({ type: 'home' }) };
      case 'design':
        return { navMode: 'back', onBack: () => setScreen({ type: 'admin' }) };
      case 'admin-card-designer':
        return { navMode: 'back', onBack: () => setScreen({ type: 'admin' }) };
      case 'admin-state-editor':
        return { navMode: 'back', onBack: () => setScreen({ type: 'admin' }) };
      case 'admin-grid-tester':
        return { navMode: 'back', onBack: () => setScreen({ type: 'admin' }) };
      case 'admin-levels':
        return { navMode: 'back', onBack: () => setScreen({ type: 'admin' }) };
      case 'creator':
        return { navMode: 'back', onBack: () => setScreen({ type: 'home' }) };
      case 'creator-editor':
        return { navMode: 'exit', onBack: () => setScreen({ type: 'creator' }) };

      case 'tutorial':
        return {
          navMode: 'exit',
          onBack: () => {
            markTutorialDone();
            setScreen({ type: 'home' });
          },
        };
      case 'setup':
        return { navMode: 'back', onBack: () => setScreen({ type: 'home' }) };
      case 'history':
        return { navMode: 'back', onBack: () => setScreen({ type: 'home' }) };
      case 'lab-setup':
        return { navMode: 'back', onBack: () => setScreen({ type: 'home' }) };
      case 'play':
        return {
          navMode: 'exit',
          onBack: () => setScreen({ type: 'home' }),
          confirmExit: gridDirty,
          onRestart: handleRestart,
        };
      case 'level':
        if (screen.mode === 'multi-sub') {
          return {
            navMode: 'exit',
            onBack: () => setScreen({ type: 'home' }),
            confirmExit: gridDirty,
            onRestart: handleRestart,
          };
        }
        if (screen.mode === 'levels') {
          return {
            navMode: 'exit',
            onBack: () => setScreen({ type: 'home' }),
            confirmExit: gridDirty,
            onRestart: handleRestart,
          };
        }
        return {
          navMode: 'exit',
          onBack: () => setScreen({ type: 'lab-setup' }),
          confirmExit: gridDirty,
          onRestart: handleRestart,
        };
    }
  })();
  const isPlaying = screen.type === 'level' || screen.type === 'play';

  function renderScreen() {
    switch (screen.type) {
      case 'admin':
        return Admin ? (
          <Suspense fallback={null}>
            <Admin onNavigate={setScreen} />
          </Suspense>
        ) : (
          <Home onNavigate={setScreen} />
        );

      case 'design':
        return DesignSystem ? (
          <Suspense fallback={null}>
            <DesignSystem />
          </Suspense>
        ) : (
          <Home onNavigate={setScreen} />
        );

      case 'admin-card-designer':
        return CardDesigner ? (
          <Suspense fallback={null}>
            <CardDesigner />
          </Suspense>
        ) : (
          <Home onNavigate={setScreen} />
        );

      case 'admin-state-editor':
        return StateEditor ? (
          <Suspense fallback={null}>
            <StateEditor />
          </Suspense>
        ) : (
          <Home onNavigate={setScreen} />
        );

      case 'admin-grid-tester':
        return GridTester ? (
          <Suspense fallback={null}>
            <GridTester />
          </Suspense>
        ) : (
          <Home onNavigate={setScreen} />
        );

      case 'admin-levels':
        return LevelManifestEditor ? (
          <Suspense fallback={null}>
            <LevelManifestEditor
              track={screen.track}
              onEditLevel={(level) => {
                setCreatorInit({ kind: 'remix', level, multi: level.puzzles.length > 1 });
                setScreen({ type: 'creator-editor' });
              }}
            />
          </Suspense>
        ) : (
          <Home onNavigate={setScreen} />
        );

      case 'creator':
        return (
          <CreatorSetup
            onStart={(init) => {
              setCreatorInit(init);
              setScreen({ type: 'creator-editor' });
            }}
          />
        );

      case 'creator-editor':
        return <Creator onNavigate={setScreen} initialConfig={creatorInit} />;

      case 'home':
        return <Home onNavigate={setScreen} />;

      case 'levels-menu':
        return <Home onNavigate={setScreen} />;

      case 'tutorial':
        return (
          <Tutorial
            key="tutorial"
            startLesson={screen.lesson}
            onLessonChange={(lesson) => {
              const s: Screen = { type: 'tutorial', lesson };
              window.history.replaceState(s, '', screenToPath(s));
            }}
            onFinish={() => {
              markTutorialDone();
              trackEvent('tutorial_all_complete');
              setScreen({ type: 'home' });
            }}
          />
        );

      case 'setup':
        return <SetupScreen />;

      case 'history':
        return <History onNavigate={setScreen} />;

      case 'lab-setup':
        return <LabScreen onNavigate={setScreen} />;

      case 'level':
        return (
          <LevelScreen
            screen={screen}
            setScreen={setScreen}
            replaceScreen={replaceScreen}
            onGridDirty={setGridDirty}
            resetTrigger={resetTrigger}
          />
        );

      case 'play':
        return (
          <PlayScreen
            screen={screen}
            setScreen={setScreen}
            onGridDirty={setGridDirty}
            resetTrigger={resetTrigger}
          />
        );
    }
  }

  const viewport = useViewport();
  const railsVisible = useViewportAtLeast(RAILS_BREAKPOINT);
  const adSlotSide = useCrossPromoSlot(screen, viewport);
  const showAdAreas = useMemo(
    () => isEnabledQueryFlag(new URLSearchParams(window.location.search), 'show_ad_areas'),
    []
  );

  const rightRail = adSlotSide === 'right' ? (
    <div className="ad-slot-iframe-wrap">
      <iframe
        src={CROSS_PROMO_SRC}
        title="Discover more games"
        loading="lazy"
        className="ad-slot-iframe"
      />
    </div>
  ) : null;

  return (
    <SceneLayout
      topBar={
        <TopBar
          navMode={topBarConfig.navMode}
          onBack={topBarConfig.onBack}
          confirmExit={topBarConfig.confirmExit}
          onRestart={topBarConfig.onRestart}
          onShowHelp={() => setShowHelp(true)}
          onTogglePrivacy={handleAnalyticsToggle}
          isPlaying={isPlaying}
        />
      }
      rightRail={rightRail}
      overflow={sceneOverflow(screen)}
      maxContentWidth={GAME_MAX_W}
      railsVisible={railsVisible}
      debugAreas={showAdAreas}
    >
      <UpdateBanner />
      {renderScreen()}
      {effectsEnabled && (
        <TransitionCanvas phase={phase} config={SCREEN_TRANSITION} />
      )}
      {showConsent && screen.type !== 'design' && (
        <ConsentScreen
          onAccept={() => handleConsentDecision('granted')}
          onDecline={() => handleConsentDecision('denied')}
        />
      )}
      {showHelp && (
        <HowToPlay
          onClose={() => setShowHelp(false)}
          onGoToTutorial={() => {
            setShowHelp(false);
            setScreen({ type: 'tutorial' });
          }}
        />
      )}
    </SceneLayout>
  );
}

/* ---------- Unified Level Screen ---------- */

function LevelScreen({
  screen,
  setScreen,
  replaceScreen,
  onGridDirty,
  resetTrigger,
}: {
  screen: Extract<Screen, { type: 'level' }>;
  setScreen: (s: Screen) => void;
  replaceScreen: (s: Screen) => void;
  onGridDirty: (dirty: boolean) => void;
  resetTrigger: number;
}) {
  const gameSettings = useGameSettings();
  const handleFreshEntryConsumed = useCallback(() => {
    if (screen.entry === 'resume' || screen.entry === 'view') return;
    replaceScreen({ ...screen, entry: 'resume' });
  }, [replaceScreen, screen]);

  if (screen.mode === 'levels') {
    const idx = screen.puzzleIndex;
    const level = LEVEL_DATA.progression.casual[idx];
    const puzzle = resolvePuzzle(level, 0);
    const isView = screen.entry === 'view';
    const viewElapsed = isView
      ? useGameStore.getState().completions[level.id]?.time
      : undefined;
    return (
      <Game
        key={`${level.id}:${screen.entry}`}
        name={`Level ${idx + 1}`}
        puzzle={puzzle}
        storageKey={level.id}
        resumeFromSave={screen.entry === 'resume'}
        viewMode={isView}
        viewElapsed={viewElapsed}
        mode="levels"
        {...gameSettings}
        onGridDirty={onGridDirty}
        resetTrigger={resetTrigger}
        onFreshEntryConsumed={handleFreshEntryConsumed}
        onBack={() => setScreen({ type: 'home' })}
        onShare={async ({ solution, grid }) => ShareService.shareOrCopy(
          await ShareService.url({ solution, grid })
        )}
        onNext={
          idx < LEVEL_DATA.progression.casual.length - 1
            ? () =>
                setScreen({
                  type: 'level',
                  mode: 'levels',
                  puzzleIndex: idx + 1,
                  entry: 'fresh',
                })
            : undefined
        }
        onWin={() => {
          trackEvent('puzzle_complete', {
            puzzle_name: level.name,
            puzzle_width: puzzle.width,
            puzzle_height: puzzle.height,
            puzzle_category: level.category,
            puzzle_index: idx,
          });
          historyService.recordCasualLevelWin(idx);
        }}
      />
    );
  }

  if (screen.mode === 'multi-sub') {
    return (
      <MultiSubLevel
        screen={screen}
        gameSettings={gameSettings}
        onGridDirty={onGridDirty}
        resetTrigger={resetTrigger}
        onFreshEntryConsumed={handleFreshEntryConsumed}
        setScreen={setScreen}
      />
    );
  }

  // Train mode
  return (
    <PuzzleLabGame
      size={screen.size}
      seed={screen.seed}
      density={screen.density}
      entry={screen.entry}
      onGridDirty={onGridDirty}
      resetTrigger={resetTrigger}
      onFreshEntryConsumed={handleFreshEntryConsumed}
      onBack={() => setScreen({ type: 'lab-setup' })}
      onShare={async ({ solution, grid }) => ShareService.shareOrCopy(
        await ShareService.url({ solution, grid })
      )}
      onRegenerate={() =>
        setScreen({
          ...screen,
          seed: nextTrainSeed(screen.seed),
          entry: 'fresh',
        })
      }
    />
  );
}

/* ---------- Multi-Sub Level (sub-puzzle play with level-complete popup) ---------- */

function MultiSubLevel({
  screen,
  gameSettings,
  onGridDirty,
  resetTrigger,
  onFreshEntryConsumed,
  setScreen,
}: {
  screen: Extract<Screen, { type: 'level'; mode: 'multi-sub' }>;
  gameSettings: ReturnType<typeof useGameSettings>;
  onGridDirty: (dirty: boolean) => void;
  resetTrigger: number;
  onFreshEntryConsumed: () => void;
  setScreen: (s: Screen) => void;
}) {
  const { levelIndex, subIndex, track, difficulty } = screen;
  const isDaily = track === 'daily';
  const pool = LEVEL_DATA[track][difficulty];
  const level = pool[levelIndex];
  const subPuzzle = level.puzzles[subIndex];
  const puzzle = resolvePuzzle(level, subIndex);
  const storageKey = `${level.id}:${subPuzzle.id}`;

  const partNumber = subIndex + 1;
  const partTotal = level.puzzles.length;
  const levelLabel = isDaily ? 'Daily Level' : `Level ${levelIndex + 1}`;
  const gameName =
    partTotal > 1
      ? `${levelLabel} \u00b7 Part ${partNumber}/${partTotal}`
      : levelLabel;

  const [levelComplete, setLevelComplete] = useState<{
    levelName: string;
    solution: boolean[][];
  } | null>(null);

  // Track whether this level was just completed so onNext can show the popup
  const levelJustCompleted = useRef(false);

  const isView = screen.entry === 'view';
  const viewElapsed = isView
    ? useGameStore.getState().completions[storageKey]?.time
    : undefined;

  return (
    <>
      <Game
        key={`${storageKey}:${screen.entry}`}
        name={gameName}
        puzzle={puzzle}
        storageKey={storageKey}
        resumeFromSave={screen.entry === 'resume'}
        viewMode={isView}
        viewElapsed={viewElapsed}
        mode="levels"
        {...gameSettings}
        onGridDirty={onGridDirty}
        resetTrigger={resetTrigger}
        onFreshEntryConsumed={onFreshEntryConsumed}
        onBack={() => setScreen({ type: 'home' })}
        onShare={async ({ solution, grid }) => ShareService.shareOrCopy(
          await ShareService.url({ solution, grid })
        )}
        onNext={() => {
          // If the whole level was just completed, show the level-complete popup
          if (levelJustCompleted.current) {
            setLevelComplete({
              levelName: level.name || levelLabel,
              solution: level.solution,
            });
            levelJustCompleted.current = false;
            return;
          }

          const { completions: c } = useGameStore.getState();
          for (let i = 0; i < level.puzzles.length; i++) {
            if (i === subIndex) continue;
            if (!isSubPuzzleComplete(c, level.id, level.puzzles[i].id)) {
              setScreen({
                type: 'level',
                mode: 'multi-sub',
                track,
                difficulty,
                levelIndex,
                subIndex: i,
                entry: 'fresh',
              });
              return;
            }
          }

          setScreen({ type: 'home' });
        }}
        onWin={() => {
          trackEvent('puzzle_complete', {
            puzzle_name: level.name,
            puzzle_width: puzzle.width,
            puzzle_height: puzzle.height,
            puzzle_category: level.category,
            puzzle_index: levelIndex,
            sub_index: subIndex,
          });
          historyService.recordMultiSubPartWin({
            track,
            difficulty,
            levelIndex,
            subIndex,
          });
          const { completions } = useGameStore.getState();
          // Check if all sub-puzzles are now complete
          const allDone = level.puzzles.every(
            (p, i) =>
              i === subIndex ||
              isSubPuzzleComplete(completions, level.id, p.id)
          );
          if (allDone) {
            let totalTime = 0;
            for (const p of level.puzzles) {
              const subKey = `${level.id}:${p.id}`;
              totalTime += completions[subKey]?.time ?? 0;
            }
            useGameStore.getState().savePuzzleCompletion(level.id, totalTime);
            levelJustCompleted.current = true;
          }
        }}
      />
      {levelComplete && (
        <LevelCompletePopup
          levelName={levelComplete.levelName}
          solution={levelComplete.solution}
          onClose={() => {
            setLevelComplete(null);
            setScreen({ type: 'home' });
          }}
        />
      )}
    </>
  );
}

/* ---------- Play Screen (shared URL entry point) ---------- */

function PlayScreen({
  screen,
  setScreen,
  onGridDirty,
  resetTrigger,
}: {
  screen: Extract<Screen, { type: 'play' }>;
  setScreen: (s: Screen) => void;
  onGridDirty: (dirty: boolean) => void;
  resetTrigger: number;
}) {
  const [decoded, setDecoded] = useState<ShareService.DecodedShare | null>(null);
  const [loading, setLoading] = useState(true);
  const [phase, setPhase] = useState<'setup' | 'playing'>('setup');
  const [settingsOverride, setSettingsOverride] =
    useState<GameSettingsOverride | null>(null);
  const [useProgress, setUseProgress] = useState(true);
  const gameSettings = useGameSettings();

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    ShareService.decode(screen.search).then((d) => {
      if (cancelled) return;
      setDecoded(d);
      setLoading(false);
    });
    return () => { cancelled = true; };
  }, [screen.search]);

  if (loading) return null; // brief flash while decompressing

  if (!decoded) {
    setScreen({ type: 'home' });
    return null;
  }

  const { puzzle: puzzleInfo, progress } = decoded;
  const puzzle = {
    width: puzzleInfo.width,
    height: puzzleInfo.height,
    difficulty: 0,
    solution: puzzleInfo.solution,
  };
  const puzzleName = 'Shared Puzzle';
  const subtitle = `${puzzle.width}\u00d7${puzzle.height} puzzle`;

  if (phase === 'setup') {
    return (
      <PlaySetupScreen
        puzzleName={puzzleName}
        subtitle={subtitle}
        puzzle={puzzle}
        progress={progress}
        onPlay={(settings, continueProgress) => {
          setSettingsOverride(settings);
          setUseProgress(continueProgress);
          setPhase('playing');
        }}
        onBack={() => setScreen({ type: 'home' })}
      />
    );
  }

  const appliedSettings = settingsOverride ?? gameSettings;
  const appliedProgress = useProgress ? progress : undefined;
  const storageKey = `shared_${screen.search}`;

  return (
    <Game
      key={`play_shared_${screen.search}`}
      name={puzzleName}
      puzzle={puzzle}
      storageKey={storageKey}
      mode="levels"
      {...appliedSettings}
      onGridDirty={onGridDirty}
      resetTrigger={resetTrigger}
      initialProgress={appliedProgress}
      onBack={() => setScreen({ type: 'home' })}
      onShare={async ({ solution, grid }) => ShareService.shareOrCopy(
        await ShareService.url({ solution, grid })
      )}
      onWin={() => {
        trackEvent('puzzle_complete', {
          puzzle_name: puzzleName,
          puzzle_width: puzzle.width,
          puzzle_height: puzzle.height,
          puzzle_category: 'community',
          puzzle_index: 0,
        });
        historyService.recordSharedWin({
          search: screen.search,
          puzzle,
          label: puzzleName,
        });
      }}
    />
  );
}
